import generatedCatalog from "./commanderSectionCatalog.generated.json";
import battleEffectLuaMap from "./battleEffectLuaMap.generated.json";

export const COMMANDER_SECTION_KEYS = [
  "titans",
  "warplane",
  "equipment",
  "antiques",
  "badges",
  "island",
  "city",
  "biochemicalWorkshop",
  "academy",
  "commanderSkills",
];

const FALLBACK_SECTION = Object.freeze({
  label: "Section",
  description: "",
  sources: [],
  groups: [],
});

const normalizeRow = (row = {}) => {
  if (!row || typeof row !== "object") return {};
  return { ...row };
};

const normalizeGroup = (group = {}) => {
  const key = String(group?.key || "").trim();
  return {
    key,
    label: String(group?.label || key || "Group"),
    count: Math.max(0, Number(group?.count || 0)),
    rows: Array.isArray(group?.rows) ? group.rows.map(normalizeRow) : [],
  };
};

const normalizeSection = (section = {}) => ({
  label: String(section?.label || "Section"),
  description: String(section?.description || ""),
  sources: Array.isArray(section?.sources) ? section.sources.map((entry) => String(entry || "")) : [],
  groups: Array.isArray(section?.groups) ? section.groups.map(normalizeGroup) : [],
});

const rawSections = generatedCatalog?.sections && typeof generatedCatalog.sections === "object"
  ? generatedCatalog.sections
  : {};

export const COMMANDER_SECTION_CATALOG = COMMANDER_SECTION_KEYS.reduce((acc, key) => {
  acc[key] = normalizeSection(rawSections[key] || FALLBACK_SECTION);
  return acc;
}, {});

const normalizeTroopRangeTerms = (value = "") => String(value || "")
  .replace(/\bClose(?:-Range)?\b/g, "Melee")
  .replace(/\bNear\b/g, "Melee")
  .replace(/\bMiddle\b/g, "Mid-Range")
  .replace(/\bMid Range\b/g, "Mid-Range")
  .replace(/\bFar\b/g, "Long-Range")
  .replace(/\bLong Range\b/g, "Long-Range");

const normalizeEffectLabel = (value = "") => normalizeTroopRangeTerms(String(value || "")
  .replace(/\(\$\d+\)/g, "")
  .replace(/\s+/g, " ")
  .trim());

const collectSectionEffectLabelById = (sections = {}) => {
  const countsById = new Map();
  const register = (effectIdRaw, rowLabelRaw) => {
    const effectId = Number(effectIdRaw || 0);
    if (!Number.isFinite(effectId) || effectId <= 0) return;
    const label = normalizeEffectLabel(rowLabelRaw);
    if (!label) return;
    const bucket = countsById.get(effectId) || new Map();
    bucket.set(label, (bucket.get(label) || 0) + 1);
    countsById.set(effectId, bucket);
  };
  const visitNode = (node, rowLabel) => {
    if (Array.isArray(node)) {
      node.forEach((entry) => visitNode(entry, rowLabel));
      return;
    }
    if (!node || typeof node !== "object") return;
    if (Object.prototype.hasOwnProperty.call(node, "effectId")) {
      register(node.effectId, rowLabel);
    }
    Object.values(node).forEach((value) => visitNode(value, rowLabel));
  };
  Object.values(sections).forEach((section) => {
    const groups = Array.isArray(section?.groups) ? section.groups : [];
    const sectionLabel = normalizeEffectLabel(section?.label || "");
    groups.forEach((group) => {
      const rows = Array.isArray(group?.rows) ? group.rows : [];
      const groupLabel = normalizeEffectLabel(group?.label || group?.key || "");
      rows.forEach((row) => {
        const rowLabel = normalizeEffectLabel(row?.label) || groupLabel || sectionLabel;
        if (!rowLabel) return;
        visitNode(row, rowLabel);
      });
    });
  });
  const map = new Map();
  countsById.forEach((labelCounts, effectId) => {
    const top = Array.from(labelCounts.entries())
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0];
    if (top?.[0]) map.set(effectId, top[0]);
  });
  return map;
};

const BATTLE_EFFECT_LUA_LABEL_BY_ID = new Map(
  (Array.isArray(battleEffectLuaMap) ? battleEffectLuaMap : [])
    .map((entry = {}) => [Number(entry?.id || 0), normalizeEffectLabel(entry?.label || "")])
    .filter(([id, label]) => Number.isFinite(id) && id > 0 && label),
);

const MANUAL_EFFECT_LABEL_BY_ID = new Map([
  [50397526, "City Skin Skill"],
]);

const SECTION_EFFECT_LABEL_BY_ID = collectSectionEffectLabelById(rawSections);

const FALLBACK_EFFECT_LABEL_BY_ID = new Map(BATTLE_EFFECT_LUA_LABEL_BY_ID);
MANUAL_EFFECT_LABEL_BY_ID.forEach((label, effectId) => {
  FALLBACK_EFFECT_LABEL_BY_ID.set(effectId, label);
});
SECTION_EFFECT_LABEL_BY_ID.forEach((label, effectId) => {
  if (!FALLBACK_EFFECT_LABEL_BY_ID.has(effectId)) {
    FALLBACK_EFFECT_LABEL_BY_ID.set(effectId, label);
  }
});

const isFallbackEffectLabel = (label, effectId) => {
  const normalized = normalizeEffectLabel(label);
  return !normalized || normalized === `Effect #${Number(effectId || 0)}`;
};

const baseEffectCatalog = Array.isArray(generatedCatalog?.effectCatalog)
  ? generatedCatalog.effectCatalog
  : [];

const mergedEffectById = new Map();
baseEffectCatalog
  .map((entry = {}) => ({
    id: Number(entry?.id || 0),
    label: normalizeEffectLabel(entry?.label || ""),
    description: String(entry?.description || ""),
  }))
  .filter((entry) => Number.isFinite(entry.id) && entry.id > 0)
  .forEach((entry) => {
    const fallbackLabel = FALLBACK_EFFECT_LABEL_BY_ID.get(entry.id) || `Effect #${entry.id}`;
    mergedEffectById.set(entry.id, {
      id: entry.id,
      label: isFallbackEffectLabel(entry.label, entry.id) ? fallbackLabel : entry.label,
      description: entry.description,
    });
  });

FALLBACK_EFFECT_LABEL_BY_ID.forEach((label, effectId) => {
  if (!mergedEffectById.has(effectId)) {
    mergedEffectById.set(effectId, {
      id: effectId,
      label,
      description: "",
    });
  }
});

export const COMMANDER_EFFECT_CATALOG = Array.from(mergedEffectById.values())
  .sort((left, right) => left.id - right.id);

export const COMMANDER_PARITY_FIELD_MAPPINGS = generatedCatalog?.parityFieldMappings && typeof generatedCatalog.parityFieldMappings === "object"
  ? generatedCatalog.parityFieldMappings
  : {};

export const getCommanderSectionCatalog = (sectionKey) => (
  COMMANDER_SECTION_CATALOG[String(sectionKey || "")] || FALLBACK_SECTION
);
